import { useState } from 'react'
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { toast } from 'sonner'
import { PASS_TYPES, formatPhp, type PlanCode } from '@naya/shared'
import { AdminLayout } from '../components/AdminLayout.js'
import { StatusPill } from '../components/StatusPill.js'
import { AuditLogList } from '../components/AuditLogList.js'
import {
  MessageTemplates,
  MessageTemplatesModal,
} from '../components/MessageTemplatesModal.js'
import { TenantEditDrawer } from '../components/TenantEditDrawer.js'
import { SuspendModal } from '../components/SuspendModal.js'
import { TenantBranches } from '../components/TenantBranches.js'
import { TenantStaff } from '../components/TenantStaff.js'
import { TenantInvoicesTab } from '../components/TenantInvoicesTab.js'
import { TenantAnalytics } from '../components/TenantAnalytics.js'
import { AdminApiError } from '../adminApi.js'
import {
  useTenantAuditLog,
  useTenantDetail,
  usePlanTiers,
} from '../hooks/useTenants.js'
import { useRegenerateSetupLink } from '../hooks/useRegenerateSetupLink.js'
import { useSendOwnerPasswordReset } from '../hooks/useSendOwnerPasswordReset.js'
import { useImpersonateOwner } from '../hooks/useTenantMutations.js'
import { fmtDate } from '../format.js'
import type { SetupLinkResponse } from '../types.js'

const PLAN_LABEL: Record<PlanCode, string> = {
  starter: 'Starter',
  pro: 'Pro',
}

const TABS = [
  'overview',
  'branches',
  'staff',
  'invoices',
  'analytics',
  'audit',
] as const
type Tab = (typeof TABS)[number]

function limit(value: number | null): string {
  return value === null ? 'Unlimited' : String(value)
}

export function TenantDetail() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const justCreated = searchParams.get('just_created') === '1'
  const tabParam = searchParams.get('tab')
  const activeTab: Tab = TABS.includes(tabParam as Tab)
    ? (tabParam as Tab)
    : 'overview'

  // Setup templates passed from TenantCreate via router state.
  const createdSetup =
    (location.state as { setup?: SetupLinkResponse } | null)?.setup ?? null

  const detail = useTenantDetail(id)
  const audit = useTenantAuditLog(id, 10)
  const planTiers = usePlanTiers()
  const regen = useRegenerateSetupLink(id)
  const sendReset = useSendOwnerPasswordReset(id)
  const impersonate = useImpersonateOwner(id)

  const [confirmRegen, setConfirmRegen] = useState(false)
  const [regenResult, setRegenResult] = useState<SetupLinkResponse | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [suspendMode, setSuspendMode] = useState<
    'suspend' | 'unsuspend' | null
  >(null)

  function setTab(tab: Tab) {
    searchParams.set('tab', tab)
    setSearchParams(searchParams, { replace: true })
  }

  if (detail.isLoading) {
    return (
      <AdminLayout>
        <p className="admin-audit-meta">Loading tenant…</p>
      </AdminLayout>
    )
  }

  if (detail.isError) {
    const notFound =
      detail.error instanceof AdminApiError && detail.error.status === 404
    return (
      <AdminLayout>
        <div className="admin-error-state">
          <h2>{notFound ? 'Tenant not found' : "Couldn't load tenant"}</h2>
          <p className="admin-audit-meta">
            {notFound
              ? "That tenant doesn't exist."
              : detail.error instanceof Error
                ? detail.error.message
                : 'Unexpected error'}
          </p>
          <button
            type="button"
            className="admin-btn"
            onClick={() => navigate('/admin/tenants')}
          >
            Back to tenants
          </button>
        </div>
      </AdminLayout>
    )
  }

  if (!detail.data) return null

  const { tenant, owner, plan_limits } = detail.data
  const tierRow = planTiers.data?.plan_tiers.find(
    (p) => p.tier === tenant.plan_tier,
  )

  function dismissJustCreated() {
    searchParams.delete('just_created')
    setSearchParams(searchParams, { replace: true })
  }

  function doRegenerate() {
    const isPasswordReset = tenant?.status !== 'pending_setup'
    const mutation = isPasswordReset ? sendReset : regen
    mutation.mutate(undefined, {
      onSuccess: (res) => {
        setConfirmRegen(false)
        setRegenResult(res)
        toast.success(
          isPasswordReset
            ? 'Password reset link generated.'
            : 'New setup link generated.',
        )
      },
      onError: (err) => {
        setConfirmRegen(false)
        toast.error(
          err instanceof AdminApiError
            ? err.userMessage
            : isPasswordReset
              ? 'Could not generate the password reset link.'
              : 'Could not regenerate the setup link.',
        )
      },
    })
  }

  function doImpersonate() {
    if (!owner) {
      toast.error('This tenant has no owner to impersonate.')
      return
    }
    // Open the destination tab SYNCHRONOUSLY, inside the click gesture, then
    // point it at the owner app once impersonation is confirmed. Calling
    // window.open() later (inside the async onSuccess) is treated as a
    // non-user-initiated popup and silently blocked on iOS Safari/Brave — which
    // is why "open in a new tab" never fired on mobile. `window.open` returns
    // null when even the gesture-time open is blocked; we then fall back to
    // navigating the current tab (the owner banner is still the way back). No
    // `noopener` so we keep the handle needed to redirect/close it (same-origin,
    // so there's no cross-origin leak concern).
    const ownerTab = window.open('about:blank', '_blank')
    impersonate.mutate(owner.id, {
      onSuccess: () => {
        // The impersonation cookie is now set alongside the admin cookie.
        if (ownerTab && !ownerTab.closed) {
          ownerTab.location.href = '/'
          toast.success(
            `Impersonating ${owner.display_name} — opened the owner app in a new tab.`,
          )
        } else {
          // Popup blocked (common on mobile) — take the current tab there.
          toast.success(`Impersonating ${owner.display_name}…`)
          window.location.href = '/'
        }
      },
      onError: (err) => {
        if (ownerTab && !ownerTab.closed) ownerTab.close()
        toast.error(
          err instanceof AdminApiError
            ? err.userMessage
            : 'Could not start impersonation.',
        )
      },
    })
  }

  return (
    <AdminLayout>
      <div className="admin-page-head">
        <div>
          <h1>{tenant.name}</h1>
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              marginTop: 8,
              flexWrap: 'wrap',
            }}
          >
            <span className="admin-audit-meta">{tenant.slug}</span>
            <StatusPill status={tenant.status} />
            <span className="admin-audit-meta">
              {PLAN_LABEL[tenant.plan_tier]} · {tenant.billing_cycle}
            </span>
            <span className="admin-audit-meta">
              created {fmtDate(tenant.created_at)}
            </span>
          </div>
        </div>
        <div className="admin-row-actions">
          <button
            type="button"
            className="admin-btn admin-btn-sm"
            onClick={() => setEditOpen(true)}
          >
            Edit
          </button>
          {tenant.status === 'suspended' ? (
            <button
              type="button"
              className="admin-btn admin-btn-sm"
              onClick={() => setSuspendMode('unsuspend')}
            >
              Unsuspend
            </button>
          ) : (
            <button
              type="button"
              className="admin-btn admin-btn-sm admin-btn-danger"
              onClick={() => setSuspendMode('suspend')}
            >
              Suspend
            </button>
          )}
          <button
            type="button"
            className="admin-btn admin-btn-sm"
            onClick={doImpersonate}
            disabled={impersonate.isPending || !owner}
            title={
              owner ? undefined : 'This tenant has no owner to impersonate'
            }
          >
            {impersonate.isPending ? 'Starting…' : 'Impersonate owner'}
          </button>
        </div>
      </div>

      {justCreated && (
        <div className="admin-callout">
          <h3>Tenant created — send the setup link</h3>
          <p className="admin-audit-meta" style={{ marginBottom: 12 }}>
            Share one of these messages with the owner via Messenger or Viber.
          </p>
          {createdSetup ? (
            <div style={{ marginBottom: 12 }}>
              <MessageTemplates
                messageEn={createdSetup.pre_filled_message_en}
                messageTl={createdSetup.pre_filled_message_tl}
                setupLink={createdSetup.setup_link}
              />
            </div>
          ) : (
            <p className="admin-audit-meta" style={{ marginBottom: 12 }}>
              The setup link is no longer in view. Use “Regenerate setup link”
              below to produce a fresh link and message.
            </p>
          )}
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            onClick={dismissJustCreated}
          >
            Got it
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            className={`admin-tab${
              activeTab === tab ? ' admin-tab-active' : ''
            }`}
            onClick={() => setTab(tab)}
          >
            {tab[0]!.toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <>
          <div className="admin-section">
            <h3>Tenant info</h3>
            <div className="admin-card">
              <dl className="admin-dl">
                <dt>Owner email</dt>
                <dd>{owner?.email ?? '—'}</dd>
                <dt>Owner name</dt>
                <dd>{owner?.display_name ?? '—'}</dd>
                <dt>Billing cycle</dt>
                <dd style={{ textTransform: 'capitalize' }}>
                  {tenant.billing_cycle}
                </dd>
                <dt>Plan tier</dt>
                <dd>
                  {PLAN_LABEL[tenant.plan_tier]}
                  {tenant.pending_plan_tier && (
                    <div className="admin-pending-note">
                      Changing to{' '}
                      {PLAN_LABEL[tenant.pending_plan_tier]} next period
                      (effective{' '}
                      {fmtDate(tenant.pending_plan_tier_effective_at)})
                    </div>
                  )}
                </dd>
                <dt>Current period</dt>
                <dd>
                  {fmtDate(tenant.current_period_start)} –{' '}
                  {fmtDate(tenant.current_period_end)}
                </dd>
                <dt>Paid through</dt>
                <dd>{fmtDate(tenant.paid_through)}</dd>
                <dt>Logo</dt>
                <dd>
                  {tenant.logo_url ? (
                    <a
                      href={tenant.logo_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {tenant.logo_url}
                    </a>
                  ) : (
                    '— (Naya default in use)'
                  )}
                </dd>
                <dt>Referrals</dt>
                <dd>{tenant.referral_entitled ? 'Entitled' : 'Not entitled'}</dd>
                <dt>Secret menu</dt>
                <dd>
                  {tenant.secret_menu_entitled ? 'Entitled' : 'Not entitled'}
                </dd>
                <dt>Notes</dt>
                <dd>{tenant.notes || '—'}</dd>
              </dl>
            </div>
          </div>

          <div className="admin-section">
            <h3>Plan limits</h3>
            <div className="admin-card">
              {tierRow ? (
                <dl className="admin-dl">
                  <dt>Plan</dt>
                  <dd>
                    {tierRow.display_name} ·{' '}
                    {formatPhp(tierRow.monthly_price_centavos)}/mo
                  </dd>
                  <dt>Max branches</dt>
                  <dd>{limit(tierRow.max_branches)}</dd>
                  <dt>Max staff / branch</dt>
                  <dd>{limit(tierRow.max_staff_per_branch)}</dd>
                  <dt>Max active customers</dt>
                  <dd>{limit(tierRow.max_active_customers)}</dd>
                  <dt>Programs / type</dt>
                  <dd>
                    {(() => {
                      const caps = tenant.program_caps ?? tierRow.program_caps
                      return PASS_TYPES.map((pt) => {
                        const c = caps[pt]
                        const v = c === null ? '∞' : c === 0 ? 'off' : String(c)
                        return `${pt}: ${v}`
                      }).join(' · ')
                    })()}
                    {tenant.program_caps ? ' (custom)' : ' (plan default)'}
                  </dd>
                </dl>
              ) : (
                <p className="admin-audit-meta">
                  Plan tier details unavailable.
                </p>
              )}
              {plan_limits.over_limits.length > 0 && (
                <div
                  className="admin-inline-error"
                  style={{ marginTop: 12 }}
                >
                  This tenant is over one or more plan limits.
                </div>
              )}
            </div>
          </div>

          <div className="admin-section">
            <div className="admin-page-head" style={{ marginBottom: 12 }}>
              <h3>Recent activity</h3>
              <Link to={`/admin/audit-log`} className="admin-audit-meta">
                Full audit log →
              </Link>
            </div>
            <div className="admin-card">
              {audit.isLoading ? (
                <p className="admin-audit-meta">Loading activity…</p>
              ) : audit.isError ? (
                <p className="admin-audit-meta">
                  Couldn't load recent activity.
                </p>
              ) : (
                <AuditLogList entries={audit.data?.events ?? []} />
              )}
            </div>
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                className="admin-btn admin-btn-sm"
                onClick={() => setConfirmRegen(true)}
              >
                {tenant.status === 'pending_setup'
                  ? 'Regenerate setup link'
                  : 'Send password reset link'}
              </button>
            </div>
          </div>
        </>
      )}

      {activeTab === 'branches' && (
        <div className="admin-section">
          <TenantBranches tenantId={id} />
        </div>
      )}

      {activeTab === 'staff' && (
        <div className="admin-section">
          <TenantStaff tenantId={id} />
        </div>
      )}

      {activeTab === 'invoices' && (
        <div className="admin-section">
          <TenantInvoicesTab tenantId={id} />
        </div>
      )}

      {activeTab === 'analytics' && <TenantAnalytics tenantId={id} />}

      {activeTab === 'audit' && (
        <div className="admin-section">
          <div className="admin-page-head" style={{ marginBottom: 12 }}>
            <h3>Activity (last 10)</h3>
            <Link to={`/admin/audit-log`} className="admin-audit-meta">
              Full audit log →
            </Link>
          </div>
          <div className="admin-card">
            {audit.isLoading ? (
              <p className="admin-audit-meta">Loading activity…</p>
            ) : audit.isError ? (
              <p className="admin-audit-meta">
                Couldn't load recent activity.
              </p>
            ) : (
              <AuditLogList entries={audit.data?.events ?? []} />
            )}
          </div>
        </div>
      )}

      {editOpen && (
        <TenantEditDrawer tenant={tenant} onClose={() => setEditOpen(false)} />
      )}

      {suspendMode && (
        <SuspendModal
          tenantId={id}
          tenantName={tenant.name}
          mode={suspendMode}
          onClose={() => setSuspendMode(null)}
        />
      )}

      {confirmRegen && (
        <div
          className="admin-modal-backdrop"
          role="presentation"
          onClick={() => setConfirmRegen(false)}
        >
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-label={
              tenant.status === 'pending_setup'
                ? 'Regenerate setup link'
                : 'Send password reset link'
            }
            style={{ maxWidth: 420 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>
              {tenant.status === 'pending_setup'
                ? 'Regenerate setup link?'
                : 'Send password reset link?'}
            </h3>
            <p style={{ margin: '10px 0 18px' }}>
              {tenant.status === 'pending_setup'
                ? 'This will revoke the existing setup link. Continue?'
                : "This will revoke any active setup link and generate a new one the owner can use to set a new password. Their current password will keep working until they consume the new link. Continue?"}
            </p>
            <div className="admin-form-actions">
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                onClick={doRegenerate}
                disabled={regen.isPending || sendReset.isPending}
              >
                {regen.isPending || sendReset.isPending
                  ? 'Working…'
                  : tenant.status === 'pending_setup'
                    ? 'Regenerate'
                    : 'Send reset link'}
              </button>
              <button
                type="button"
                className="admin-btn"
                onClick={() => setConfirmRegen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {regenResult && (
        <MessageTemplatesModal
          title="New setup link"
          messageEn={regenResult.pre_filled_message_en}
          messageTl={regenResult.pre_filled_message_tl}
          setupLink={regenResult.setup_link}
          onClose={() => setRegenResult(null)}
        />
      )}
    </AdminLayout>
  )
}
